/**
 * PATH: apps/qa/src/lib/inspection/trades-and-templates.ts
 * PURPOSE: Categories, project types, trade definitions, quick templates, floor/area templates, wizard steps
 */

import {
  Home, Building, Building2, Landmark, Shield, RefreshCw, Wrench,
  Zap, Droplets, Wind, Flame, Waves, Thermometer, Sun, Paintbrush,
} from 'lucide-react'
import type {
  CategoryKey, ProjectTypeKey, CategoryInfo, ProjectTypeInfo,
  TradeGroupInfo, TradeInfo,
} from './types'
import type { FloorTemplate } from '@quotech/job-hub/jobs/qa/archivedStructureTemplates'

export { QUICK_TEMPLATES, type QuickTemplate, type FloorTemplate } from '@quotech/job-hub/jobs/qa/archivedStructureTemplates'

// ── Categories ───────────────────────────────────────────────────────────────

export const CATEGORY_INFO: Record<CategoryKey, CategoryInfo> = {
  residential: {
    name: 'Residential', description: 'Homes, apartments, and other dwelling buildings', icon: Home,
    suggestedClasses: ['Class 1a', 'Class 1b', 'Class 2', 'Class 4', 'Class 10a'],
    suggestedTrades: ['Electrical', 'Plumbing', 'Structural'],
  },
  commercial: {
    name: 'Commercial', description: 'Offices, retail, warehouses, and business buildings', icon: Building2,
    suggestedClasses: ['Class 5', 'Class 6', 'Class 7a', 'Class 7b', 'Class 8'],
    suggestedTrades: ['Electrical', 'HVAC', 'Fire Safety', 'Interior Fit-out'],
  },
  infrastructure: {
    name: 'Infrastructure', description: 'Public buildings, healthcare, education, and community facilities', icon: Landmark,
    suggestedClasses: ['Class 3', 'Class 9a', 'Class 9b', 'Class 9c'],
    suggestedTrades: ['Electrical', 'HVAC', 'Fire Safety', 'Plumbing'],
  },
}

// ── Project Types ────────────────────────────────────────────────────────────

export const PROJECT_TYPE_INFO: Record<ProjectTypeKey, ProjectTypeInfo> = {
  new_construction: { name: 'New Construction', description: 'Building from ground up - all new work', icon: Building },
  renovation: { name: 'Renovation', description: 'Upgrading or modifying existing structures', icon: RefreshCw },
  remediation: { name: 'Remediation', description: 'Fixing defects, damage, or compliance issues', icon: Wrench },
}

// ── Trade Tags ───────────────────────────────────────────────────────────────

export const TRADE_GROUPS: Record<string, TradeGroupInfo> = {
  'Core Services': { trades: ['Electrical', 'Plumbing', 'HVAC'], icon: Zap },
  'Structure & Envelope': { trades: ['Structural', 'Roofing', 'Cladding', 'Waterproofing', 'Glazing'], icon: Building },
  'Safety & Compliance': { trades: ['Fire Safety', 'Insulation'], icon: Shield },
  'Finishes': { trades: ['Interior Fit-out', 'Painting', 'Tiling'], icon: Paintbrush },
  'Civil & External': { trades: ['Civil', 'Landscaping'], icon: Landmark },
}

export const TRADE_INFO: Record<string, TradeInfo> = {
  'Electrical': { name: 'Electrical', icon: Zap, color: 'text-yellow-500' },
  'Plumbing': { name: 'Plumbing', icon: Droplets, color: 'text-blue-500' },
  'HVAC': { name: 'HVAC', icon: Wind, color: 'text-cyan-500' },
  'Structural': { name: 'Structural', icon: Building, color: 'text-gray-500' },
  'Fire Safety': { name: 'Fire Safety', icon: Flame, color: 'text-red-500' },
  'Waterproofing': { name: 'Waterproofing', icon: Waves, color: 'text-blue-400' },
  'Insulation': { name: 'Insulation', icon: Thermometer, color: 'text-orange-500' },
  'Roofing': { name: 'Roofing', icon: Sun, color: 'text-amber-500' },
  'Cladding': { name: 'Cladding', icon: Building2, color: 'text-slate-500' },
  'Glazing': { name: 'Glazing', icon: Building2, color: 'text-sky-500' },
  'Interior Fit-out': { name: 'Interior Fit-out', icon: Paintbrush, color: 'text-purple-500' },
  'Painting': { name: 'Painting', icon: Paintbrush, color: 'text-pink-500' },
  'Tiling': { name: 'Tiling', icon: Landmark, color: 'text-teal-500' },
  'Civil': { name: 'Civil', icon: Landmark, color: 'text-emerald-500' },
  'Landscaping': { name: 'Landscaping', icon: Sun, color: 'text-green-500' },
}

// ── Floor & Area Templates ───────────────────────────────────────────────────

export const FLOOR_TEMPLATES: FloorTemplate[] = [
  { name: 'Basement', areas: ['Plant Room', 'Storage', 'Car Park'] },
  { name: 'Ground Floor', areas: ['Entry', 'Reception', 'Amenities'] },
  { name: 'Level 1', areas: ['Office Area', 'Meeting Room', 'Kitchen'] },
  { name: 'Level 2', areas: ['Open Plan', 'Break Room'] },
  { name: 'Level 3', areas: ['Open Plan', 'Server Room'] },
  { name: 'Rooftop', areas: ['Plant Area', 'Access Paths'] },
]

export const AREA_TEMPLATES: Record<string, string[]> = {
  residential: ['Living Room', 'Kitchen', 'Bedroom', 'Bathroom', 'Laundry', 'Garage', 'Balcony', 'Entry'],
  commercial: ['Reception', 'Open Plan', 'Meeting Room', 'Kitchen', 'Amenities', 'Server Room', 'Storage'],
  wet_areas: ['Bathroom', 'Ensuite', 'Kitchen', 'Laundry', 'WC', 'Shower'],
  common: ['Corridor', 'Stairwell', 'Lift Lobby', 'Fire Exit', 'Plant Room', 'Electrical Room'],
}

// ── Wizard Steps ─────────────────────────────────────────────────────────────

export const INSPECTION_STEPS = [
  { id: 'job', label: 'Select Job', icon: Building2 },
  { id: 'details', label: 'Details', icon: Zap },
  { id: 'structure', label: 'Floors & Areas', icon: Landmark },
  { id: 'review', label: 'Review', icon: Shield },
] as const
